import { useState, useEffect } from 'react';
import { Camera, MapPin, Send } from 'lucide-react';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const trails = [
  "Monkey Business",
  "Red Line",
  "Drumul Rosu",
  "CL Over",
  "Westreme",
  "Killy's Backyard",
  "Respawn"
];

interface LocationMarker {
  lat: number;
  lng: number;
}

function LocationMarkerComponent({ position, onLocationSelect }: { 
  position: LocationMarker | null;
  onLocationSelect: (location: LocationMarker) => void;
}) {
  const map = useMapEvents({
    click(e) {
      const newPosition = { lat: e.latlng.lat, lng: e.latlng.lng };
      onLocationSelect(newPosition);
    },
  });

  useEffect(() => {
    if (position) {
      map.setView([position.lat, position.lng], map.getZoom());
    }
  }, [position, map]);

  return position ? <Marker position={position} /> : null;
}

function InitialLocationSetter({ onLocationFound }: { onLocationFound: (location: LocationMarker) => void }) {
  const map = useMap();

  useEffect(() => {
    map.locate({
      setView: true,
      maxZoom: 16
    });

    map.on('locationfound', (e) => {
      onLocationFound({ lat: e.latlng.lat, lng: e.latlng.lng });
    });

    map.on('locationerror', () => {
      // Default to Brașov center if location access is denied
      const defaultLocation = { lat: 45.6427, lng: 25.5887 };
      onLocationFound(defaultLocation);
    });
  }, [map, onLocationFound]);

  return null;
}

export default function ReportProblem() {
  const [formData, setFormData] = useState({
    trail: '',
    description: '',
    email: '',
  });
  const [location, setLocation] = useState<LocationMarker | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!location || !formData.trail || !formData.description || !formData.email) {
      alert('Te rugăm să completezi toate câmpurile obligatorii');
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload images first
      const imageUrls = await Promise.all(
        images.map(async (image) => {
          const imageRef = ref(storage, `reports/${Date.now()}_${image.name}`);
          await uploadBytes(imageRef, image);
          return getDownloadURL(imageRef);
        })
      );

      // Save report to Firestore
      await addDoc(collection(db, 'problems'), {
        ...formData,
        location: { lat: location.lat, lng: location.lng },
        images: imageUrls,
        createdAt: new Date(),
        status: 'new'
      });

      setSubmitSuccess(true);
      setFormData({ trail: '', description: '', email: '' });
      setLocation(null);
      setImages([]);
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('A apărut o eroare. Te rugăm să încerci din nou.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newImages = Array.from(e.target.files);
      if (images.length + newImages.length <= 2) {
        setImages([...images, ...newImages]);
      } else {
        alert('Poți încărca maxim 2 imagini');
      }
    }
  };

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Send className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Mulțumim pentru raportare!</h2>
          <p className="text-gray-600 mb-6">
            Am primit sesizarea ta și o vom analiza cât mai curând posibil.
          </p>
          <button
            onClick={() => setSubmitSuccess(false)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Raportează altă problemă
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Raportează o problemă</h1>
        
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Traseu *
            </label>
            <select
              value={formData.trail}
              onChange={(e) => setFormData({ ...formData, trail: e.target.value })}
              className="w-full rounded-lg border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              required
            >
              <option value="">Selectează traseul</option>
              {trails.map((trail) => (
                <option key={trail} value={trail}>{trail}</option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Locație *
            </label>
            <div className="h-[400px] rounded-lg overflow-hidden border border-gray-300">
              <MapContainer
                center={[45.6427, 25.5887]}
                zoom={13}
                className="h-full w-full"
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <InitialLocationSetter onLocationFound={setLocation} />
                <LocationMarkerComponent position={location} onLocationSelect={setLocation} />
              </MapContainer>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Click pe hartă pentru a ajusta locația problemei
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descriere problemă *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full rounded-lg border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              rows={4}
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fotografii (opțional, maxim 2)
            </label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg cursor-pointer hover:bg-gray-100">
                <Camera className="w-5 h-5 text-gray-600" />
                <span className="text-sm text-gray-600">Adaugă fotografii</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
              {images.length > 0 && (
                <span className="text-sm text-gray-600">
                  {images.length} {images.length === 1 ? 'imagine selectată' : 'imagini selectate'}
                </span>
              )}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full rounded-lg border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Se trimite...' : 'Trimite raportarea'}
          </button>
        </form>
      </div>
    </div>
  );
}